import { UserListItem } from '../../../apis/user-management/user-management-api.def.ts'
import { ColDef, ColumnResizedEvent, GridApi } from 'ag-grid-community'
import { IdeyaAgGridTable } from '../../../common/ag-grid/ideya-ag-grid-table.tsx'
import { GridReadyEvent } from 'ag-grid-enterprise'
import { useAppSelector } from '../../../redux/hooks.ts'
import { useCallback, useMemo, useRef } from 'react'
import { AgGridReact, CustomCellRendererProps } from 'ag-grid-react'
import { USER_MANAGEMENT_COLUMN_STATE_KEY } from '../../../utils/constants.ts'
import { marketDateTimezoneFormatter } from '../../../utils/formatters.ts'
import { selectUser } from '../../../features/user/user.slice.ts'
import { CalendarOutlined, EditOutlined, MailOutlined } from '@ant-design/icons'
import { IdeyaButton } from '../../../common/antd/ideya-button/ideya-button.tsx'
import { selectManagementInput } from '../../../redux/slices/management-input.slice.ts'
import { AccountType } from '../../../enums/management-input.enum.ts'
import {
  canCreateUserPermissions,
  hasPermission,
} from '../../../utils/permissions.ts'
import { useTranslation } from 'react-i18next'
import { IsTrialCellRenderer } from '../../components/is-trial-cell-renderer.tsx'
import { useIsSuperViewer } from '../../../common/hooks/use-is-superviewer.ts'
import {
  getOrderedColumnsFromState,
  isPersistableColumnResize,
} from '../../../utils/ag-grid-table-helpers.ts'
import { BlockLeaveTab } from './user-modal/user-block-leave/user-block-leave-combined-modal.tsx'
import { useFixedBreakpoint } from '@/common/hooks/use-is-mobile.ts'
import './user-management-table.scss'

interface UserManagementTableProps {
  isLoading: boolean
  isFetching: boolean
  userList: UserListItem[]
  onEditUser: (id: string) => void
  onInviteUser: (id: string) => void
  onOpenBlockLeave: (id: string, tab: BlockLeaveTab) => void
  height: number | string
}

export const UserManagementTable: React.FC<UserManagementTableProps> = ({
  isLoading,
  isFetching,
  userList,
  onEditUser,
  onInviteUser,
  onOpenBlockLeave,
  height,
}) => {
  const gridApiRef = useRef<GridApi | null>(null)
  const gridRef = useRef<AgGridReact>(null)
  const user = useAppSelector(selectUser)
  const managementInput = useAppSelector(selectManagementInput)
  const { t } = useTranslation()
  const isSuperViewer = useIsSuperViewer()
  const screens = useFixedBreakpoint()

  const canEditUser = hasPermission(
    canCreateUserPermissions,
    user.firmRole,
    user.groupRole,
    user.role
  )

  const columns: ColDef[] = useMemo(() => {
    const baseColumns: ColDef[] = [
      ...(!isSuperViewer
        ? [
            {
              headerName: '',
              field: 'id',
              width: !screens.md ? 120 : 80,
              minWidth: !screens.md ? 120 : 80,
              maxWidth: !screens.md ? 120 : 80,
              suppressHeaderMenuButton: true,
              cellRenderer: (props: CustomCellRendererProps) => {
                if (
                  props.data.createAdminId === user?.sub ||
                  user?.role === 'Master'
                ) {
                  return (
                    <div className={'user-management-table__action-cell'}>
                      <IdeyaButton
                        ideyaType={'bw'}
                        icon={<EditOutlined />}
                        onClick={() => onEditUser(props.data.id)}
                      />
                      <IdeyaButton
                        ideyaType={'bw'}
                        icon={<CalendarOutlined />}
                        onClick={() => onOpenBlockLeave(props.data.id, 'apply')}
                      />
                    </div>
                  )
                } else {
                  return <></>
                }
              },
            },
          ]
        : []),
      {
        headerName: '',
        field: 'isTrial',
        cellRenderer: IsTrialCellRenderer,
        width: 80,
      },
      {
        headerName: t('user_management.user_table.columns.full_name'),
        field: 'fullName',
      },
      {
        headerName: t('user_management.user_table.columns.email'),
        field: 'email',
      },
      {
        headerName: t('user_management.user_table.columns.firm'),
        field: 'firm.name',
      },
      {
        headerName: t('user_management.user_table.columns.firm_role'),
        field: 'firmRole',
      },
      {
        headerName: t('user_management.user_table.columns.group'),
        field: 'group.name',
      },
      {
        headerName: t('user_management.user_table.columns.sub_region'),
        field: 'subRegion',
      },

      {
        headerName: t('user_management.user_table.columns.group_role'),
        field: 'groupRole',
      },
      {
        headerName: t('user_management.user_table.columns.state'),
        field: 'status',
      },
      {
        headerName: t('user_management.user_table.columns.primary_book'),
        field: 'primaryBook.name',
      },
      {
        headerName: t('user_management.user_table.columns.account_type'),
        field: 'accountType',
      },
      {
        headerName: t('user_management.user_table.columns.last_login_time'),
        field: 'lastLoginTime',
        valueFormatter: (params) =>
          marketDateTimezoneFormatter(
            {
              ...params,
              data: {
                ...params.data,
                marketTimeZone:
                  // Browser timezone
                  Intl.DateTimeFormat().resolvedOptions().timeZone,
              },
            },
            { showTimezone: true }
          ),
      },
      {
        headerName: t('user_management.user_table.columns.last_idea_send_time'),
        field: 'lastIdeaSendTime',
        valueFormatter: (params) =>
          marketDateTimezoneFormatter(
            {
              ...params,
              data: {
                ...params.data,
                marketTimeZone:
                  // Browser timezone
                  Intl.DateTimeFormat().resolvedOptions().timeZone,
              },
            },
            { showTimezone: true }
          ),
      },
    ]

    // Conditionally add columns
    if (managementInput.accountType === AccountType.External && canEditUser) {
      const isEmailIndex = baseColumns.findIndex(
        (column) => column.field === 'email'
      )
      baseColumns.splice(isEmailIndex + 1, 0, {
        headerName: t('user_management.user_table.columns.send_invite_email'),
        field: 'sendInviteEmail',
        cellRenderer: (props: CustomCellRendererProps) => {
          if (
            props.data.createAdminId === user?.sub ||
            user?.role === 'Master'
          ) {
            return (
              <IdeyaButton
                ideyaType={'bw'}
                icon={<MailOutlined />}
                onClick={() => onInviteUser(props.data.id)}
              />
            )
          }
          return <></>
        },
      })
    }

    if (managementInput.accountType === AccountType.Internal) {
      const groupRoleIndex = baseColumns.findIndex(
        (column) => column.field === 'groupRole'
      )
      baseColumns.splice(groupRoleIndex + 1, 0, {
        headerName: 'Internal Role',
        field: 'internalRole',
      })
    }

    const savedState = localStorage.getItem(USER_MANAGEMENT_COLUMN_STATE_KEY)
    const orderedColumns = getOrderedColumnsFromState(
      savedState,
      baseColumns,
      gridApiRef.current
    )
    if (orderedColumns) return orderedColumns

    return baseColumns
  }, [
    t,
    managementInput.accountType,
    canEditUser,
    user,
    onEditUser,
    onInviteUser,
    onOpenBlockLeave,
  ])

  const handleGridReady = useCallback((params: GridReadyEvent) => {
    gridApiRef.current = params.api
    if (!gridRef.current) return
    const savedState = localStorage.getItem(USER_MANAGEMENT_COLUMN_STATE_KEY)
    if (savedState) {
      const state = JSON.parse(savedState)
      gridRef.current.api.applyColumnState({
        state: state,
        applyOrder: true,
      })
    }
  }, [])

  const handleColumnChanged = () => {
    if (!gridRef.current) return
    const state = gridRef.current.api.getColumnState()
    localStorage.setItem(
      USER_MANAGEMENT_COLUMN_STATE_KEY,
      JSON.stringify(state)
    )
  }

  const handleSortChanged = () => {
    if (!gridRef.current) return
    const state = gridRef.current.api.getColumnState()
    localStorage.setItem(
      USER_MANAGEMENT_COLUMN_STATE_KEY,
      JSON.stringify(state)
    )
  }

  const handleColumnResized = (event: ColumnResizedEvent) => {
    // Persist manual drag and the column menu's autosize actions.
    if (!gridRef.current || !isPersistableColumnResize(event)) return
    const state = gridRef.current.api.getColumnState()
    localStorage.setItem(
      USER_MANAGEMENT_COLUMN_STATE_KEY,
      JSON.stringify(state)
    )
  }

  const defaultColDef = useMemo<ColDef>(
    () => ({
      resizable: true,
      filter: false,
      initialWidth: 200,
      mainMenuItems: (params) => {
        return params.defaultItems.map((item) => {
          if (item === 'resetColumns') {
            return {
              name: 'Reset Columns',
              action: () => {
                if (localStorage.getItem(USER_MANAGEMENT_COLUMN_STATE_KEY)) {
                  localStorage.removeItem(USER_MANAGEMENT_COLUMN_STATE_KEY)
                }

                gridRef.current?.api.resetColumnState()
              },
            }
          }
          return item
        })
      },
    }),
    []
  )

  return (
    <IdeyaAgGridTable
      ref={gridRef}
      columns={columns}
      defaultColDef={defaultColDef}
      rowData={userList}
      loading={isLoading || isFetching}
      onGridReady={handleGridReady}
      onColumnMoved={handleColumnChanged}
      onColumnPinned={handleColumnChanged}
      onColumnVisible={handleColumnChanged}
      onColumnResized={handleColumnResized}
      onSortChanged={handleSortChanged}
      height={height}
      canCopy={true}
    />
  )
}
